import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Spinner, EmptyState, usePagination, Pagination, Badge, attendanceBadgeMap, sessionTypeBadgeMap, SearchableSelect } from '../../components/UI';
import { KpiCard, SectionIcon } from '../../components/StudentUI';
import { fmtDate, fmtSessionName } from '../../utils/format';

const fmtSessionTime = t => {
  if (!t) return null;
  const parts = String(t).split(':');
  return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : String(t);
};

const sessionTypeKey = value => String(value || '').trim().toLowerCase();

const ReadOnlyNote = ({ children }) => (
  <div className="stu-readonly-note">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
    </svg>
    {children}
  </div>
);

export default function StudentAttendancePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [intakeCode, setIntakeCode] = useState('');
  const [modules, setModules] = useState([]);
  const [moduleCode, setModuleCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/me/intakes').then(res => {
      if (cancelled) return;
      const rows = res.data.data || [];
      setIntakes(rows);
      const primary = rows.find(r => r.status === 'active') || rows[0];
      if (primary) setIntakeCode(primary.intake_code);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setModuleCode('');
    setModules([]);
    if (!intakeCode) return;
    api.get(`/me/intakes/${intakeCode}/modules`).then(res => {
      if (!cancelled) setModules(res.data.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [intakeCode]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (intakeCode) params.intake_code = intakeCode;
    if (moduleCode) params.module_code = moduleCode;
    api.get('/me/attendance', { params })
      .then(res => { if (!cancelled) setData(res.data.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [intakeCode, moduleCode]);

  const s = data?.summary;
  const records = data?.records || [];
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(records);

  if (loading) return <div className="stu-page"><Spinner /></div>;

  return (
    <>
      <div className="stu-topbar">
        <div className="stu-topbar-title">My Attendance</div>
        <div className="stu-topbar-sub">Read-only summary of attendance recorded by your trainers</div>
      </div>
      <div className="stu-page">
        <ReadOnlyNote>This reflects attendance recorded via QR code clock-in/out at each session.</ReadOnlyNote>

        <div className="d-flex gap-3 flex-wrap" style={{ marginBottom: 14 }}>
          {intakes.length > 1 && (
            <div style={{ maxWidth: 320, flex: '1 1 240px' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake</label>
              <SearchableSelect value={intakeCode} onChange={e => setIntakeCode(e.target.value)}
                options={intakes.map(i => ({
                  value: i.intake_code,
                  label: i.intake_code,
                }))} />
            </div>
          )}
          {modules.length > 0 && (
            <div style={{ maxWidth: 320, flex: '1 1 240px' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module</label>
              <SearchableSelect value={moduleCode} onChange={e => setModuleCode(e.target.value)}
                placeholder="-- All modules --"
                options={modules.map(m => ({ value: m.module_code, label: m.module_name }))} />
            </div>
          )}
        </div>

        <div className="stu-stat-row-4">
          <KpiCard label="Present" icon="bi-check-circle" color="var(--sms-dot-present)" value={<span className="stu-stat-value present" style={{ marginTop: 0 }}>{s?.present || 0}</span>} />
          <KpiCard label="Late" icon="bi-clock-history" color="var(--sms-dot-late)" value={<span className="stu-stat-value warn" style={{ marginTop: 0 }}>{s?.late || 0}</span>} />
          <KpiCard label="Absent" icon="bi-x-circle" color="var(--sms-dot-absent)" value={<span className="stu-stat-value absent" style={{ marginTop: 0 }}>{s?.absent || 0}</span>} />
          <KpiCard label="MC" icon="bi-file-medical" color="var(--sms-dot-mc)" value={<span className="stu-stat-value mc" style={{ marginTop: 0 }}>{s?.mc || 0}</span>} />
        </div>

        <div className="stu-card">
          <div className="stu-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-calendar-check" color="var(--sms-stat-blue)" />Session history</h3></div>
          <div className="stu-card-body" style={{ padding: records.length ? 0 : undefined }}>
            {records.length === 0 ? (
              <EmptyState icon="bi-calendar-check" message="No attendance records yet" />
            ) : (
              <>
                <table className="stu-table">
                  <thead>
                    <tr><th>Session</th><th>Session Type</th><th>Date</th><th>Start Time</th><th>Venue</th><th>Trainer</th><th className="num">Status</th></tr>
                  </thead>
                  <tbody>
                    {paged.map(r => (
                      <tr key={r.id}>
                        <td>{fmtSessionName(r) || '—'}</td>
                        <td>{sessionTypeKey(r.session_type)
                          ? <Badge value={sessionTypeKey(r.session_type)} map={sessionTypeBadgeMap} />
                          : '—'}</td>
                        <td>{fmtDate(r.session_date)}</td>
                        <td>{fmtSessionTime(r.start_time) || '—'}</td>
                        <td>{r.venue_name || '—'}</td>
                        <td>{r.trainer_name || '—'}</td>
                        <td className="num">
                          <Badge value={r.status} map={attendanceBadgeMap} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
